/**
 * "Latest request wins" — a ticket counter for imperative scene lifecycles
 * that dispatch overlapping async work (e.g. `assets/world-scene.ts`'s
 * `setRegion`, which awaits a network fetch per distinct object ref inside
 * `buildWorldInstances`). The existing per-owner `disposed` flag pattern
 * used throughout this app's scene modules (`three-model-scene.ts`,
 * `world-scene.ts`, `flight/flight-scene.ts`) only answers "has the whole
 * scene been torn down" — it says nothing about whether *this particular*
 * call is still the most recent one for whatever it's building. Without
 * that, a slow older call can finish after a faster newer one and still
 * apply its (stale) result on top, leaving whatever the newer call built
 * orphaned — never referenced, never disposed. `begin()`/`isCurrent()`
 * closes that gap: pure bookkeeping, no DOM/GPU involved, so it's testable
 * on its own (`test/latest-request-guard.test.ts`) — see `world-scene.ts`'s
 * `applyRegion` for the GPU-side half of the fix this enables (discarding
 * a stale call's built `THREE.Group` via `disposeTree` instead of adding
 * it to the scene).
 */
export class LatestRequestGuard {
  #current = 0;

  /** Starts a new request and returns its ticket. */
  begin(): number {
    this.#current += 1;
    return this.#current;
  }

  /** True if `ticket` is still the most recently begun request (no later `begin()` call has happened since). */
  isCurrent(ticket: number): boolean {
    return ticket === this.#current;
  }
}
